import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Pencil, Trash2, LogOut, Tag, DollarSign, Inbox, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  adminGetPricing, adminCreatePricing, adminUpdatePricing, adminDeletePricing,
  adminGetCoupons, adminCreateCoupon, adminUpdateCoupon, adminDeleteCoupon,
  adminGetCallbacks,
} from "../lib/api";

const TABS = [
  { id: "pricing", label: "Pricing", icon: DollarSign },
  { id: "coupons", label: "Coupons", icon: Tag },
  { id: "callbacks", label: "Callbacks", icon: Inbox },
];

const EMPTY_PRICE = { service_name: "", description: "", regular_price: "", sale_price: "", category: "Smog Check", display_order: 0, active: true };
const EMPTY_COUPON = { title: "", description: "", discount_type: "$", discount_value: "", code: "", start_date: "", expiration_date: "", terms: "", display_order: 0, active: true };

export default function Admin() {
  const { token, user, loading, signOut } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("pricing");

  useEffect(() => {
    if (!loading && !user) nav("/admin/login");
  }, [loading, user, nav]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center bg-paper"><Loader2 className="h-8 w-8 animate-spin text-volt" /></div>;
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <header className="bg-white border-b border-line sticky top-0 z-30">
        <div className="section flex items-center justify-between py-3">
          <div className="flex items-center gap-2">
            <img src="/logo.webp" alt="Smog Hut Modesto" className="h-9 w-auto" />
            <span className="font-head font-extrabold text-ink">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-ink/60 font-body hidden sm:block">{user.email}</span>
            <button onClick={() => { signOut(); nav("/admin/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm font-head font-semibold text-ink hover:text-star"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="section py-8">
        <div className="flex gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-head font-semibold text-sm transition-colors ${tab === t.id ? "bg-volt text-white" : "bg-white text-ink border border-line hover:border-volt"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "pricing" && <PricingTab token={token} />}
        {tab === "coupons" && <CouponsTab token={token} />}
        {tab === "callbacks" && <CallbacksTab token={token} />}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-head font-semibold text-ink/70">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls = "w-full rounded-lg border border-line px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-volt";

function PricingTab({ token }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => adminGetPricing(token).then(setItems).catch(() => toast.error("Failed to load pricing")), [token]);
  useEffect(() => { load(); }, [load]);

  const save = async (data) => {
    try {
      if (data.id) await adminUpdatePricing(token, data.id, data);
      else await adminCreatePricing(token, data);
      toast.success("Saved — live on the website now");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };
  const toggle = async (it) => { try { await adminUpdatePricing(token, it.id, { ...it, active: !it.active }); load(); } catch { toast.error("Update failed"); } };
  const del = async (it) => { if (window.confirm(`Delete "${it.service_name}"?`)) { try { await adminDeletePricing(token, it.id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } } };

  if (!items) return <Loader2 className="h-6 w-6 animate-spin text-volt" />;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...EMPTY_PRICE })} data-testid="add-price-btn" className="btn-volt !py-2"><Plus className="h-4 w-4" /> Add Service</button>
      </div>
      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-deep text-white text-left"><tr>
            <th className="px-4 py-3 font-head">Service</th><th className="px-4 py-3 font-head">Category</th>
            <th className="px-4 py-3 font-head">Price</th><th className="px-4 py-3 font-head">Coupon</th>
            <th className="px-4 py-3 font-head">Active</th><th className="px-4 py-3 font-head text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-line/60 last:border-0" data-testid={`admin-price-${it.id}`}>
                <td className="px-4 py-3 font-semibold text-ink">{it.service_name}</td>
                <td className="px-4 py-3 text-ink/70">{it.category}</td>
                <td className="px-4 py-3">{it.regular_price || "—"}</td>
                <td className="px-4 py-3 text-openg">{it.sale_price || "—"}</td>
                <td className="px-4 py-3"><button onClick={() => toggle(it)} data-testid={`toggle-price-${it.id}`} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${it.active ? "bg-openg/15 text-openg" : "bg-ink/10 text-ink/50"}`}>{it.active ? "Active" : "Hidden"}</button></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(it)} className="p-1.5 text-volt hover:bg-volt-dim rounded-lg" data-testid={`edit-price-${it.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(it)} className="p-1.5 text-star hover:bg-star/10 rounded-lg" data-testid={`del-price-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Service" : "Add Service"} onClose={() => setEditing(null)}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Service name"><input className={inputCls} value={editing.service_name} onChange={(e) => setEditing({ ...editing, service_name: e.target.value })} data-testid="price-form-name" /></Field>
            <Field label="Category"><input className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></Field>
            <Field label="Regular price"><input className={inputCls} value={editing.regular_price} onChange={(e) => setEditing({ ...editing, regular_price: e.target.value })} placeholder="39.95 or Call" /></Field>
            <Field label="With-coupon price"><input className={inputCls} value={editing.sale_price} onChange={(e) => setEditing({ ...editing, sale_price: e.target.value })} placeholder="29.95 or Call" /></Field>
            <Field label="Display order"><input type="number" className={inputCls} value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })} /></Field>
            <Field label="Active"><select className={inputCls} value={editing.active ? "1" : "0"} onChange={(e) => setEditing({ ...editing, active: e.target.value === "1" })}><option value="1">Active</option><option value="0">Hidden</option></select></Field>
          </div>
          <Field label="Description"><textarea className={inputCls} rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
          <button onClick={() => save(editing)} data-testid="price-form-save" className="btn-volt w-full mt-2">Save</button>
        </Modal>
      )}
    </div>
  );
}

function CouponsTab({ token }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => adminGetCoupons(token).then(setItems).catch(() => toast.error("Failed to load coupons")), [token]);
  useEffect(() => { load(); }, [load]);

  const clean = (d) => ({ ...d, start_date: d.start_date || null, expiration_date: d.expiration_date || null });
  const save = async (data) => {
    try {
      if (data.id) await adminUpdateCoupon(token, data.id, clean(data));
      else await adminCreateCoupon(token, clean(data));
      toast.success("Saved — live on the website now");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const toggle = async (it) => { try { await adminUpdateCoupon(token, it.id, clean({ ...it, active: !it.active })); load(); } catch { toast.error("Update failed"); } };
  const del = async (it) => { if (window.confirm(`Delete "${it.title}"?`)) { try { await adminDeleteCoupon(token, it.id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } } };

  if (!items) return <Loader2 className="h-6 w-6 animate-spin text-volt" />;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...EMPTY_COUPON })} data-testid="add-coupon-btn" className="btn-volt !py-2"><Plus className="h-4 w-4" /> Add Coupon</button>
      </div>
      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-deep text-white text-left"><tr>
            <th className="px-4 py-3 font-head">Title</th><th className="px-4 py-3 font-head">Discount</th>
            <th className="px-4 py-3 font-head">Code</th><th className="px-4 py-3 font-head">Expires</th>
            <th className="px-4 py-3 font-head">Active</th><th className="px-4 py-3 font-head text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-line/60 last:border-0" data-testid={`admin-coupon-${it.id}`}>
                <td className="px-4 py-3 font-semibold text-ink">{it.title}</td>
                <td className="px-4 py-3">{it.discount_type === "%" ? `${it.discount_value}%` : `$${it.discount_value}`}</td>
                <td className="px-4 py-3 font-mono text-xs">{it.code || "—"}</td>
                <td className="px-4 py-3 text-ink/70">{it.expiration_date || "None"}</td>
                <td className="px-4 py-3"><button onClick={() => toggle(it)} data-testid={`toggle-coupon-${it.id}`} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${it.active ? "bg-openg/15 text-openg" : "bg-ink/10 text-ink/50"}`}>{it.active ? "Active" : "Hidden"}</button></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(it)} className="p-1.5 text-volt hover:bg-volt-dim rounded-lg" data-testid={`edit-coupon-${it.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(it)} className="p-1.5 text-star hover:bg-star/10 rounded-lg" data-testid={`del-coupon-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Coupon" : "Add Coupon"} onClose={() => setEditing(null)}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title"><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="coupon-form-title" /></Field>
            <Field label="Code"><input className={inputCls} value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} /></Field>
            <Field label="Discount type"><select className={inputCls} value={editing.discount_type} onChange={(e) => setEditing({ ...editing, discount_type: e.target.value })}><option value="$">$ Amount</option><option value="%">% Percent</option></select></Field>
            <Field label="Discount value"><input className={inputCls} value={editing.discount_value} onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })} /></Field>
            <Field label="Start date"><input type="date" className={inputCls} value={editing.start_date || ""} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} /></Field>
            <Field label="Expiration date"><input type="date" className={inputCls} value={editing.expiration_date || ""} onChange={(e) => setEditing({ ...editing, expiration_date: e.target.value })} /></Field>
            <Field label="Display order"><input type="number" className={inputCls} value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })} /></Field>
            <Field label="Active"><select className={inputCls} value={editing.active ? "1" : "0"} onChange={(e) => setEditing({ ...editing, active: e.target.value === "1" })}><option value="1">Active</option><option value="0">Hidden</option></select></Field>
          </div>
          <Field label="Description"><textarea className={inputCls} rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
          <Field label="Terms"><textarea className={inputCls} rows={2} value={editing.terms} onChange={(e) => setEditing({ ...editing, terms: e.target.value })} /></Field>
          <button onClick={() => save(editing)} data-testid="coupon-form-save" className="btn-volt w-full mt-2">Save</button>
        </Modal>
      )}
    </div>
  );
}

function CallbacksTab({ token }) {
  const [items, setItems] = useState(null);
  useEffect(() => { adminGetCallbacks(token).then(setItems).catch(() => toast.error("Failed to load")); }, [token]);
  if (!items) return <Loader2 className="h-6 w-6 animate-spin text-volt" />;
  if (!items.length) return <div className="card-soft p-10 text-center text-ink/60 font-body">No callback requests yet.</div>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {items.map((c) => (
        <div key={c.id} className="card-soft p-5" data-testid={`callback-${c.id}`}>
          <div className="flex items-center justify-between">
            <h3 className="font-head font-bold text-ink">{c.name}</h3>
            <span className="text-xs text-ink/50 font-body">{(c.created_at || "").slice(0, 10)}</span>
          </div>
          <p className="mt-1 text-sm text-volt font-semibold">{c.phone}</p>
          {c.email && <p className="text-sm text-ink/70">{c.email}</p>}
          {c.message && <p className="mt-2 text-sm text-ink/70 font-body">{c.message}</p>}
        </div>
      ))}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-deep/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-head font-bold text-xl text-ink">{title}</h2>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-line"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
